import {useMemo} from 'react'
import {calculateTotalAccesses} from '@/features/projects/utils'
import {calculateTotalElaboration, reportingAccessTypes} from '@/features/planned-accesses/utils'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {AccessType} from '@/features/access-types/types'
import {AccessFrequency} from '@/features/planned-accesses/types'

/**
 * Interface representing an in-person access item with frequency
 */
interface InPersonAccessItem {
    accessType?: AccessType | null
    numberOfAccesses?: number | string
    frequency: AccessFrequency
}

/**
 * Interface representing a generic access item
 */
interface AccessItem {
    accessType?: AccessType | null
    numberOfAccesses?: number | string
}

/**
 * Interface representing a processing access item with validated data
 */
interface ProcessingAccessItem {
    accessType?: AccessType
    numberOfAccesses: number
}

interface PlannedAccessesFormCountsProps {
    projectId?: string
    absenceAccesses?: AccessItem[]
    inPersonAccesses?: InPersonAccessItem[]
    singleAccesses?: AccessItem[]
    durationDays?: number
}

/**
 * Hook to calculate and manage all access counts for the planned accesses
 *
 * This hook calculates various totals including:
 * - Total treatment accesses
 * - Processing accesses
 * - Absence accesses
 * - In-presence accesses
 * - Psychosocial and medical accesses
 * - Overall total accesses
 * - Total psychosocial and medical accesses
 * - Total reporting accesses
 * - Unaccounted accesses
 *
 * @param props - Object containing access values and projectId
 * @returns Object containing project data and all calculated access counts
 */
export const usePlannedAccessesFormCounts = ({
    projectId,
    absenceAccesses = [],
    inPersonAccesses = [],
    singleAccesses = [],
    durationDays
}: PlannedAccessesFormCountsProps) => {
    const {data: project, isLoading: isProjectLoading} = useGetProject({
        urlParams: {projectId: projectId || ''},
        params: {responseType: 'extended'},
        options: {enabled: !!projectId}
    })

    const currentDurationDays = durationDays || project?.durationDays

    /**
     * Filter out unaccounted access types from absenceAccesses
     */
    const unaccountedAccessTypes = [
        'short_extension_processing',
        'short_team_meeting',
        'initial_project_evaluation'
    ] as const

    const filteredAbsenceAccesses = useMemo(() => {
        return absenceAccesses.filter(
            access =>
                !access.accessType ||
                !(unaccountedAccessTypes as readonly string[]).includes(access.accessType.accessType)
        )
    }, [absenceAccesses])

    const unaccountedAbsenceAccesses = useMemo(() => {
        return absenceAccesses.filter(
            access =>
                access.accessType &&
                (unaccountedAccessTypes as readonly string[]).includes(access.accessType.accessType)
        )
    }, [absenceAccesses])

    /**
     * Calculate total treatment accesses based on frequency and project duration
     */
    const totalTreatmentAccessesCount = useMemo(
        () =>
            inPersonAccesses.reduce((acc: number, curr: InPersonAccessItem) => {
                const frequency = curr.frequency as AccessFrequency
                return (
                    acc +
                    calculateTotalAccesses(currentDurationDays ?? 0, frequency, Number(curr.numberOfAccesses) || 0)
                )
            }, 0),
        [inPersonAccesses, currentDurationDays]
    )

    /**
     * Extract and normalize processing accesses from both single and absence accesses
     */
    const processingAccesses = useMemo(() => {
        const result = [
            ...singleAccesses
                .filter((singleAccess: AccessItem) => singleAccess.accessType?.nonInPersonAccess === 'processing')
                .map((singleAccess: AccessItem) => {
                    if (!singleAccess.accessType) {
                        return null
                    }
                    return {
                        accessType: singleAccess.accessType,
                        numberOfAccesses: Number(singleAccess.numberOfAccesses) || 0
                    }
                })
                .filter(Boolean)
        ] as ProcessingAccessItem[]

        return result
    }, [singleAccesses, absenceAccesses])

    /**
     * Calculate total processing accesses using the utility function
     */
    const totalProcessingAccesses = useMemo(() => {
        return calculateTotalElaboration(processingAccesses)
    }, [processingAccesses])

    /**
     * Calculate total absences including processing accesses (excluding unaccounted)
     */
    const totalAbsencesAccessesCount = useMemo(() => {
        return (
            filteredAbsenceAccesses.reduce((acc: number, curr: AccessItem) => {
                return acc + (Number(curr.numberOfAccesses) || 0)
            }, 0) + totalProcessingAccesses
        )
    }, [filteredAbsenceAccesses, totalProcessingAccesses])

    /**
     * Calculate total single accesses
     */
    const totalSingleAccessesCount = useMemo(() => {
        return singleAccesses.reduce((acc: number, curr: AccessItem) => {
            return acc + (Number(curr.numberOfAccesses) || 0)
        }, 0)
    }, [singleAccesses])

    /**
     * Calculate total in-presence accesses (treatment + single)
     */
    const totalInPresenceAccessCount = useMemo(() => {
        return totalTreatmentAccessesCount + totalSingleAccessesCount
    }, [totalTreatmentAccessesCount, totalSingleAccessesCount])

    /**
     * Calculate overall total accesses (in-presence + absences, excluding unaccounted)
     */
    const totalAccessesCount = useMemo(() => {
        return totalInPresenceAccessCount + totalAbsencesAccessesCount
    }, [totalInPresenceAccessCount, totalAbsencesAccessesCount])

    /**
     * Calculate total psychosocial and medical accesses
     */
    const totalMedicalAccessesCount = useMemo(() => {
        const psychosocialMedicalTreatmentAccessesTotal = inPersonAccesses
            .filter(access => access.accessType?.siarAccessType == 'medical_access')
            .reduce((acc: number, curr: InPersonAccessItem) => {
                const frequency = curr.frequency as AccessFrequency
                return (
                    acc +
                    calculateTotalAccesses(currentDurationDays ?? 0, frequency, Number(curr.numberOfAccesses) || 0)
                )
            }, 0)

        const psychosocialMedicalSingleAccessesTotal = singleAccesses
            .filter(access => access.accessType?.siarAccessType == 'medical_access')
            .reduce((acc: number, curr: AccessItem) => {
                return acc + (Number(curr.numberOfAccesses) || 0)
            }, 0)

        return psychosocialMedicalTreatmentAccessesTotal + psychosocialMedicalSingleAccessesTotal
    }, [inPersonAccesses, singleAccesses, currentDurationDays])

    const totalPsychosocialAccessesCount = useMemo(() => {
        const psychosocialMedicalTreatmentAccessesTotal = inPersonAccesses
            .filter(access => access.accessType?.siarAccessType == 'psychosocial_access')
            .reduce((acc: number, curr: InPersonAccessItem) => {
                const frequency = curr.frequency as AccessFrequency
                return (
                    acc +
                    calculateTotalAccesses(currentDurationDays ?? 0, frequency, Number(curr.numberOfAccesses) || 0)
                )
            }, 0)

        const psychosocialMedicalSingleAccessesTotal = singleAccesses
            .filter(access => access.accessType?.siarAccessType == 'psychosocial_access')
            .reduce((acc: number, curr: AccessItem) => {
                return acc + (Number(curr.numberOfAccesses) || 0)
            }, 0)

        return psychosocialMedicalTreatmentAccessesTotal + psychosocialMedicalSingleAccessesTotal
    }, [inPersonAccesses, singleAccesses, currentDurationDays])

    const totalReportingAccessCount = useMemo(() => {
        return filteredAbsenceAccesses
            .filter(
                absenceAccess =>
                    absenceAccess.accessType && reportingAccessTypes.includes(absenceAccess.accessType.accessType)
            )
            .reduce((acc: number, curr: AccessItem) => {
                return acc + (Number(curr.numberOfAccesses) || 0)
            }, 0)
    }, [filteredAbsenceAccesses])

    /**
     * Calculate total unaccounted accesses
     */
    const unaccountedTotal = useMemo(() => {
        return unaccountedAbsenceAccesses.reduce((acc: number, curr: AccessItem) => {
            return acc + (Number(curr.numberOfAccesses) || 0)
        }, 0)
    }, [unaccountedAbsenceAccesses])

    return {
        project,
        isProjectLoading,
        processingAccesses,
        totalProcessingAccesses,
        totalTreatmentAccessesCount,
        totalAbsencesAccessesCount,
        totalInPresenceAccessCount,
        totalAccessesCount,
        totalMedicalAccessesCount,
        totalPsychosocialAccessesCount,
        totalReportingAccessCount,
        unaccountedTotal
    }
}
